"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  SignupFlowSlug,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/constants";
import { useSignupFlowStore, getRehydrationRedirect } from "@./state";
import {
  buildRedirectUrl,
} from "apps/website/utils/abandoned-cart/buildRedirectUrl";

const INITIAL_SLUG = SignupFlowSlug.CAT_COUNT;

// Module-level state survives StrictMode double-fire (same JS lifecycle)
// but resets on hard navigation (fresh page load).
type GuardState =
  | { status: "idle" }
  | { status: "redirecting"; target: string }
  | { status: "done" };

let guardState: GuardState = { status: "idle" };

export function resetGuardForTests(): void {
  guardState = { status: "idle" };
}

/**
 * Rehydration guard for returning users entering the SUF via localStorage.
 *
 * After localStorage rehydration, runs the shared rehydration redirect logic
 * (`getRehydrationRedirect`) which checks version mismatch first, then finds
 * the earliest incomplete page before RECIPES.
 *
 * The same function is called by the AC restore flow so both entry paths use
 * identical decision logic.
 *
 * Returns `isGuardComplete` so the layout can hold the loading screen until
 * the guard has finished evaluating.
 */
export function useSignupFlowRehydrationGuard(): { isGuardComplete: boolean } {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [ isGuardComplete, setIsGuardComplete ] = useState(false);

  const {
    isStoreInitialised,
    getActiveCatIds,
  } = useSignupFlowStore((state) => ({
    isStoreInitialised: state.isStoreInitialised,
    getActiveCatIds: state.getActiveCatIds,
  }));

  // Reset the guard when the flow layout unmounts (user leaves the SUF via
  // client-side navigation). This ensures the guard re-runs on the next entry.
  useEffect(() => {
    return () => {
      guardState = { status: "idle" };
    };
  }, []);

  useEffect(() => {
    if (!isStoreInitialised) {
      return;
    }

    if (guardState.status === "done") {
      setIsGuardComplete(true);
      return;
    }

    // A redirect is in flight (StrictMode re-fire or waiting for nav).
    // Keep the loading screen until the pathname matches the target.
    if (guardState.status === "redirecting") {
      if (pathname === guardState.target) {
        guardState = { status: "done" };
        setIsGuardComplete(true);
      }
      return;
    }

    // Only run the redirect when the user enters the flow on the initial
    // page (i.e. via a CTA, ad, or redirect). Landing directly on a
    // specific page means the user navigated there intentionally.
    if (pathname !== INITIAL_SLUG) {
      guardState = { status: "done" };
      setIsGuardComplete(true);
      return;
    }

    // New users on the start page have no store data to validate.
    if (getActiveCatIds().length === 0) {
      guardState = { status: "done" };
      setIsGuardComplete(true);
      return;
    }

    const { slug } = getRehydrationRedirect();

    if (slug && slug !== pathname) {
      guardState = { status: "redirecting", target: slug };
      router.replace(buildRedirectUrl(slug, searchParams));
      return;
    }

    guardState = { status: "done" };
    setIsGuardComplete(true);
  }, [
    isStoreInitialised,
    pathname,
    router,
    searchParams,
    getActiveCatIds,
  ]);

  return { isGuardComplete };
}
